<script setup>
import {NuxtLink} from '#components'
const route = useRoute()
const router = useRouter()
const sPoints = useState('points')
const sAccount = useState('account')
const sFavorite = useState('favorite')
const { notification } = useNotifications()

const {
  getSettings,
  getPoint,
  getFavorite,
  addFavorite,
  deleteFavorite,
  getProducer,
  getInspections,
  addBrowsingHistory,
} = useDatabase()
const cuid = route.params.cuid

const { consumption_point } = await getSettings('service')
const producer    = await getProducer(cuid)
const inspections = await getInspections(producer.id)
sFavorite.value   = await getFavorite(producer.id)

const confDialog = ref(false)
const images = computed(() => producer.image_files?.map(v => v?.url).filter(v => v) || [])

const fileNameByURL = (url) => url?.split('/').reverse()[0] || ''

const onChangeFavorite = async () => {
  if (sAccount.value) {
    const actionFavorite = sFavorite.value ? deleteFavorite : addFavorite
    sFavorite.value = await actionFavorite(producer.id)
    console.log(sFavorite.value)
  } else {
    router.push({
      path: '/login',
      query: { redirect_to: route.path }
    })
  }
}

const onAddBrowsingHistory = async () => {
  await addBrowsingHistory(cuid).then(async resp => {
    sPoints.value = await getPoint().then(({total}) => total)
    router.replace(route.path+'/detail')
    notification.success(consumption_point+'ポイントを消費しました。\nページ下部にて、詳細情報が閲覧可能です。', 3)
  }).catch(e => {
    confDialog.value = false
    notification.error(e.data.message, 3)
    // alert(e.data.message)
  })
}

const onBuyPoint = () => {
  const btnPurchasePointElement = document.querySelector('#btn-purchase-point')
  if (btnPurchasePointElement) btnPurchasePointElement.click()
}

const topCategories = (categories) => categories.filter(v => !v.parent_id).filter(v => v.id != producer.top_category)
const topCategory = computed(() => producer.categories.find(v => v.id == producer.top_category))
</script>

<template>

  <h2 class="font-noto-serif mb-6 mt-8 lg:mt-16 px-3 sm:px-6 text-center text-xl sm:text-3xl leading-tight">{{ producer.catch_copy }}</h2>

  <default-page-producer-slider
    v-if="images?.length || []"
    :images="images"
  />

  <default-ui-main>
    <div class="max-w-[800px] mx-auto">
      <div
        v-if="producer.categories?.length"
        class="mt-3 mb-6 flex flex-wrap justify-start sm:justify-center gap-x-2 gap-y-1 items-center"
      >
        <NuxtLink
          v-if="topCategory"
          :to="'/?c='+topCategory.id+'#product-list'"
          class="badge badge-orange badge-lg badge-pill badge-outlined"
        >{{ topCategory.name }}</NuxtLink>

        <NuxtLink
          v-for="(category, c) of topCategories(producer.categories)"
          :key="c"
          :to="'/?c='+category.id+'#product-list'"
          class="badge badge-zinc badge-lg badge-pill badge-outlined"
        >{{ category.name }}</NuxtLink>
      </div>

      <table class="table-list w-full mb-10 lg:mb-16">
        <tbody>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="badge badge-lg badge-secondary w-full font-noto-serif">地域</span>
            </th>
            <td class="px-3 !py-2 sm:pr-0 text-lg font-noto-serif">
              <p>{{ producer.prefecture }}/{{ producer.address1 }}</p>
              <p v-if="producer.nearby_info">{{ producer.nearby_info }}</p>
            </td>
          </tr>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="badge badge-lg badge-secondary w-full font-noto-serif">取扱品目</span>
            </th>
            <td
              v-if="producer.product_detail"
              class="px-3 !py-2 sm:pr-0 text-lg font-noto-serif"
            ><p v-html="producer.product_detail.replace(/\n/g, '<br>')"></p></td>
          </tr>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="badge badge-lg badge-secondary w-full font-noto-serif">メッセージ</span>
            </th>
            <td class="px-3 !py-2 sm:pr-0 text-lg font-noto-serif" v-html="producer.message"></td>
          </tr>
        </tbody>
      </table>


      <div class="mb-10 lg:mb-16" v-if="inspections.length">
        <h3 class="h3-type-a mb-6">検査結果など</h3>

        <ul>
          <li
            v-for="(insp, i) of inspections"
            :key="i"
            class="mb-3"
          >
            <component
              :is="insp.source_file ? NuxtLink : 'h4'"
              :to="insp.source_file ? insp.source_file?.url : null"
              :target="insp.source_file ? '_blank' : null"
              class="font-noto-serif mb-1"
              :class="{'!underline': insp.source_file}"
            >{{ fileNameByURL(insp.source_file?.path) || insp.name }}</component>
            <p class="ml-6 font-noto-serif">{{ insp.description }}</p>
          </li>
        </ul>
      </div>

      <div class="flex items-center flex-col sm:flex-row gap-x-6 gap-y-3 max-w-600 w-full mx-auto">
        <button
          type="button"
          class="btn btn-primary sm:flex-1 !text-xl"
          :class="{ 'btn-outlined': sAccount && !sFavorite }"
          @click="onChangeFavorite"
        >
          <template v-if="sAccount">
            <span class="font-noto-serif" v-if="sFavorite">お気に入りから削除</span>
            <span v-else class="font-noto-serif">お気に入り店舗へ追加</span>
          </template>
          <span v-else class="font-noto-serif">お気に入り店舗へ追加</span>
        </button>

        <button
          v-if="sAccount"
          type="button"
          class="btn btn-primary sm:flex-1 !text-xl"
          @click="sPoints >= consumption_point ? confDialog = true : onBuyPoint()"
        >
          ご注文先はこちら
          <span
            class="!text-lg !px-4 badge badge-outlined font-noto-serif"
            :class="sPoints < consumption_point ? '!border-white !text-white' : 'badge-primary'"
          >{{ consumption_point }}pt</span>
        </button>

        <NuxtLink
          v-else
          :to="'/login?redirect_to='+route.path"
          class="btn btn-primary sm:flex-1 !text-xl"
        >
          ご注文先はこちら
          <span class="!text-lg !px-4 badge badge-outlined font-noto-serif badge-primary">{{ consumption_point }}pt</span>
        </NuxtLink>
      </div>
    </div>

    <default-ui-dialog
      v-if="sAccount"
      v-model="confDialog"
      content-center
      :width="600"
    >

      <div class="text-center font-noto-serif mb-6">
        詳細ページを閲覧すると、100ポイント消費します。<br>
        よろしいですか？
      </div>

      <div class="w-full flex justiy-center gap-x-6">
        <button
          :disabled="sPoints < 100"
          type="button"
          class="btn btn-sm btn-primary btn-outlined flex-1 !text-xl"
          @click="confDialog = false"
        >キャンセル</button>
        <button
          :disabled="sPoints < 100"
          type="button"
          class="btn btn-sm btn-primary flex-1 !text-xl"
          @click="onAddBrowsingHistory"
        >はい</button>
      </div>
    </default-ui-dialog>

  </default-ui-main>
</template>